import {
  JSCANIFY_SCRIPT_URL,
  OPENCV_LOCAL_SCRIPT_PATH,
  OPENCV_SCRIPT_URL,
} from 'components/Documents/Scanner/constants';

const OPENCV_SCRIPT_ID = 'opencv-script';
const JSCANIFY_SCRIPT_ID = 'jscanify-script';

const loadScript = (src: string, id: string): Promise<void> =>
  new Promise((resolve, reject) => {
    const existing = document.getElementById(id) as HTMLScriptElement | null;
    if (existing?.src === src || existing?.src.endsWith(src)) {
      resolve();
      return;
    }
    existing?.remove();

    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });

/** Skip local path when dev server would return index.html (causes "Unexpected token '<'"). */
const isLocalScriptAvailable = async (src: string): Promise<boolean> => {
  if (src.startsWith('http://') || src.startsWith('https://')) {
    return true;
  }

  try {
    const response = await fetch(src, { method: 'HEAD', cache: 'no-store' });
    const contentType = response.headers.get('content-type') ?? '';
    return response.ok && !contentType.includes('text/html');
  } catch {
    return false;
  }
};

const isOpenCvReady = (): boolean => {
  const cv = window.cv as { Mat?: unknown; onRuntimeInitialized?: () => void } | undefined;
  return Boolean(cv?.Mat);
};

const waitForOpenCvRuntime = (): Promise<void> =>
  new Promise((resolve, reject) => {
    if (isOpenCvReady()) {
      resolve();
      return;
    }

    const timeout = window.setTimeout(() => {
      reject(new Error('OpenCV runtime initialization timed out'));
    }, 45000);

    const cv = window.cv;
    if (!cv) {
      window.clearTimeout(timeout);
      reject(new Error('OpenCV is not available on window'));
      return;
    }

    cv.onRuntimeInitialized = () => {
      window.clearTimeout(timeout);
      resolve();
    };
  });

const loadOpenCv = async (): Promise<void> => {
  const candidates = [OPENCV_SCRIPT_URL, OPENCV_LOCAL_SCRIPT_PATH];
  let lastError: Error | null = null;

  for (const src of candidates) {
    const available = await isLocalScriptAvailable(src);
    if (!available) {
      continue;
    }

    try {
      await loadScript(src, OPENCV_SCRIPT_ID);
      await waitForOpenCvRuntime();
      return;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      document.getElementById(OPENCV_SCRIPT_ID)?.remove();
      delete (window as { cv?: unknown }).cv;
    }
  }

  throw lastError ?? new Error('Failed to load OpenCV');
};

let loadPromise: Promise<void> | null = null;

/** Loads OpenCV.js and jscanify once per app session. */
export const loadScannerLibraries = (): Promise<void> => {
  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = (async () => {
    await loadOpenCv();
    await loadScript(JSCANIFY_SCRIPT_URL, JSCANIFY_SCRIPT_ID);

    if (!window.jscanify) {
      throw new Error('jscanify failed to initialize');
    }
  })();

  return loadPromise;
};
